import React, { useCallback, useEffect, useMemo } from 'react';

import PageAnchorList from '@/components/PageAnchorList';
import SidebarNavRail from '@/components/SidebarNavRail';
import type { SidebarNavItem } from '@/components/SidebarNavRail/types';
import { SUB_SIDEBAR_Z_CLASS } from '@/constants/layout';
import { useNarrowNav } from '@/contexts/narrowNavContext';
import { usePageAnchors } from '@/contexts/pageAnchorsContext';
import { useSidebarLayout } from '@/contexts/sidebarLayoutContext';
import usePageAnchorFromUrl from '@/hooks/usePageAnchorFromUrl';
import usePageAnchorJump from '@/hooks/usePageAnchorJump';
import useUserSetting from '@/hooks/useUserSetting';
import sortNavItems from '@/utils/sortNavItems';

import type { Props } from './types';

/**
 * The second desktop rail: whichever section you are inside gets one, immediately right of the
 * workspace rail.
 *
 * This is the piece every section shares — it takes its width, position and collapse state from
 * the sidebar layout context, and registers itself there while mounted so page content only
 * reserves room for a rail that is actually on screen. A section supplies its own branding, its
 * links, and which link owns the current route.
 *
 * It has no collapse toggle of its own: the workspace rail's toggle collapses both rails.
 *
 * The entry that owns the current route also carries a second level: whatever on-page anchors the
 * open page registered through `usePageAnchor`, each one a jump to that card. Pages that register
 * nothing get the plain single-level rail. A caret on that entry folds the anchors away, and
 * jumping to one writes it into the url so the address bar is a link straight to that card; the
 * rail is always mounted inside a section, so it is also what lands a link like that on its card.
 *
 * Its pages are listed A–Z, which the narrow drawer picks up too since it reads them from here.
 *
 * The rail is also where the narrow layout gets its section menu from: it is the one place that
 * already knows a section's links and which of them owns the route, so it publishes itself rather
 * than leaving the drawer to assemble a second copy. The markup below is hidden under `lg`, but
 * the component mounts at every width, so the registration holds on a phone.
 *
 * Callers: PersonSidebarNav, BillingSidebarNav.
 */
const SubNavRail = ({ ariaLabel, title, header, collapsedHeader, items, currentLabel }: Props) => {
  const { railsCollapsed, railWidthClass, subRailLeftClass, setSubRailVisible } =
    useSidebarLayout();
  const { anchors, activeAnchorId } = usePageAnchors();
  const { registerSection } = useNarrowNav();
  const [anchorsCollapsed, setAnchorsCollapsed] = useUserSetting('navAnchorsCollapsed', false);
  const jumpToAnchor = usePageAnchorJump();

  usePageAnchorFromUrl();

  const sortedItems = useMemo(() => sortNavItems(items), [items]);

  const toggleAnchorsCollapsed = useCallback(
    () => setAnchorsCollapsed(!anchorsCollapsed),
    [anchorsCollapsed, setAnchorsCollapsed]
  );

  useEffect(() => {
    setSubRailVisible(true);

    return () => setSubRailVisible(false);
  }, [setSubRailVisible]);

  // No dependency list: the context drops an update that changes nothing, which is cheaper to get
  // right than keeping a list in step with a `header` that is a new element on every render.
  useEffect(() => {
    registerSection({ ariaLabel, title, header, items: sortedItems, currentLabel });
  });

  // Declared second so it is the last cleanup to run, and only on the way out.
  useEffect(() => () => registerSection(null), [registerSection]);

  const isCurrent = useCallback(
    (path: string) => items.some((item) => item.path === path && item.label === currentLabel),
    [currentLabel, items]
  );

  const renderSubItems = useCallback(
    (_item: SidebarNavItem, current: boolean) =>
      current && anchors.length ? (
        <PageAnchorList anchors={anchors} activeAnchorId={activeAnchorId} onJump={jumpToAnchor} />
      ) : null,
    [activeAnchorId, anchors, jumpToAnchor]
  );

  return (
    <SidebarNavRail
      ariaLabel={ariaLabel}
      collapsed={railsCollapsed}
      widthClass={railWidthClass}
      leftClass={subRailLeftClass}
      zIndexClass={SUB_SIDEBAR_Z_CLASS}
      surfaceClassName="bg-gray-50"
      items={sortedItems}
      isCurrent={isCurrent}
      header={header}
      collapsedHeader={collapsedHeader}
      renderSubItems={renderSubItems}
      subItemsCollapsed={anchorsCollapsed}
      onToggleSubItems={toggleAnchorsCollapsed}
    />
  );
};

export default React.memo(SubNavRail);
